import { useCallback } from 'react';

import { useUpToDateValue } from '#@/shared/hooks/useUpToDateValue';

import { initStateValue } from './initStateValue';
import type {
  CompatibleStorageValue,
  MigrationStrategy,
  Parse,
  Stringify,
  TransformValueOnRead,
} from './types';

export function useInitStorageStateValue<
  T extends CompatibleStorageValue,
  PersistedParsedValue extends CompatibleStorageValue = T,
>({
  unversionedKey,
  migrationStrategy,
  version,
  duration,
  stringify,
  transformValueOnRead,
  parse,
  emptyValue,
  when,
}: {
  unversionedKey: string;
  version: 1 | 2;
  duration: number;
  migrationStrategy: MigrationStrategy<T>;
  transformValueOnRead: TransformValueOnRead<T, PersistedParsedValue>;
  stringify: Stringify;
  parse: Parse;
  emptyValue: T;
  when: boolean;
}) {
  const whenRef = useUpToDateValue(when ?? true);

  const getInitState = useCallback(
    () =>
      initStateValue({
        whenRef,
        unversionedKey,
        version,
        duration,
        migrationStrategy,
        transformValueOnRead,
        emptyValue,
        parse,
        stringify,
      }),
    [
      duration,
      emptyValue,
      migrationStrategy,
      parse,
      stringify,
      transformValueOnRead,
      unversionedKey,
      version,
      whenRef,
    ],
  );

  return { getInitState, whenRef };
}
